import React, { PureComponent } from 'react'
import {connect} from 'react-redux'
import Button from '@material-ui/core/Button'
import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import DialogContentText from '@material-ui/core/DialogContentText'
import DialogTitle from '@material-ui/core/DialogTitle'
import LinearProgress from '@material-ui/core/LinearProgress'
import * as cornerstone from "cornerstone-core"
import * as cornerstoneWADOImageLoader from "cornerstone-wado-image-loader"
import {
  filesStore,
} from '../actions'
import { 
  getDicomPatientName,
  getDicomStudyId,
  getDicomStudyDate,
  getDicomStudyTime,
  getDicomStudyDescription,
  getDicomSeriesDate,
  getDicomSeriesTime,
  getDicomSeriesDescription,
  getDicomSeriesNumber,
  getDicomInstanceNumber,
  getDicomNumberOfFrames,
  getDicomSliceLocation,
  getDicomSliceDistance,
  getDicomRows,
  getDicomColumns,
  getDicomEchoNumber,
  getDicomModality,
  getFileNameCorrect,
  dicomDateTimeToLocale,
} from '../functions'
import { t } from '../i18n'
import { applySeriesTotals } from '../utils/loadStudyFromLocalCache'
import { stampNumberOfFramesOnFile } from '../utils/seriesImageCounts'
import {
  indexLocalFilesBySeries,
  resolveSeriesTotalFromIndex,
} from '../utils/indexLocalFilesBySeries'
import { readImageType, stampIsScoutOnFileItem } from '../utils/isScoutSeries'

cornerstoneWADOImageLoader.external.cornerstone = cornerstone

function getSeriesInstanceUID(image) {
  try {
    if (image && image.data && typeof image.data.string === 'function') {
      return image.data.string('x0020000e') || ''
    }
  } catch (e) {
    /* ignore */
  }
  return ''
}

class OpenMultipleFilesDlg extends PureComponent {

  state = {
    progress: 0,
    phase: 'index', // index | load
    cancel: false,
  }  

  componentDidMount() {
    this.items = []
    this.count = 0
    this.step = 0
    this._lastPublishCount = 0
    this._seriesIndex = null
    this.t0 = performance.now()
    this.startLoadPipeline()
  }

  startLoadPipeline = async () => {
    const files = this.props.files
    if (!files || !files.length) {
      this.close()
      return
    }

    try {
      this.setState({ phase: 'index', progress: 0 })
      this._seriesIndex = await indexLocalFilesBySeries(files, {
        onProgress: (done, total) => {
          if (this.state.cancel) return
          this.setState({
            phase: 'index',
            progress: Math.floor((done * 100) / total),
          })
          if (typeof this.props.onProgress === 'function') {
            this.props.onProgress({
              phase: 'index',
              completed: done,
              totalTasks: total,
              progress: Math.floor((done * 100) / total),
            })
          }
        },
      })
    } catch (err) {
      console.warn('OpenMultipleFilesDlg - index series failed:', err)
      this._seriesIndex = null
    }

    if (this.state.cancel) {
      this.props.setFilesStore(null)
      this.close()
      return
    }

    this.setState({ phase: 'load', progress: 0 })
    if (typeof this.props.onProgress === 'function') {
      this.props.onProgress({
        phase: 'load',
        completed: 0,
        totalTasks: files.length,
        progress: 0,
      })
    }
    this.step = files.length / 50
    this.nextProgress = this.step

    const imageIds = []
    for (let i = 0; i < files.length; i++) {
      const file = files[i]
      if (this.props.origin === 'local') {
        imageIds.push(cornerstoneWADOImageLoader.wadouri.fileManager.add(file))
      } else {
        imageIds.push(
          cornerstoneWADOImageLoader.wadouri.fileManager.addBuffer(file.data)
        )
      }
    }

    for (let i = 0; i < files.length; i++) {
      const file = files[i]
      if (this.state.cancel) {
        this.props.setFilesStore(null)
        this.close()
        return
      }

      cornerstone.loadImage(imageIds[i]).then(
        (image) => {
          if (this.state.cancel) return

          const patientName = getDicomPatientName(image)
          const studyId = getDicomStudyId(image)
          const studyDate = getDicomStudyDate(image)
          const studyTime = getDicomStudyTime(image)
          const studyDescription = getDicomStudyDescription(image)
          const seriesDate = getDicomSeriesDate(image)
          const seriesTime = getDicomSeriesTime(image)
          const seriesDescription = getDicomSeriesDescription(image)
          const seriesNumber = getDicomSeriesNumber(image)
          const seriesInstanceUID = getSeriesInstanceUID(image)
          const instanceNumber = getDicomInstanceNumber(image)
          const sliceDistance = getDicomSliceDistance(image)
          const echoNumber = getDicomEchoNumber(image)
          const sliceLocation = getDicomSliceLocation(image)
          const columns = getDicomColumns(image)
          const rows = getDicomRows(image)
          const modality = getDicomModality(image)
          const imageType = readImageType(image)
          const studyDateTime =
            studyDate === undefined
              ? undefined
              : dicomDateTimeToLocale(`${studyDate}.${studyTime}`)

          const indexedTotal = resolveSeriesTotalFromIndex(
            this._seriesIndex,
            seriesInstanceUID,
            seriesNumber
          )
          const numberOfFrames = getDicomNumberOfFrames(image) || 0
          const totalImages =
            indexedTotal != null
              ? Math.max(indexedTotal, numberOfFrames > 1 ? numberOfFrames : 0)
              : numberOfFrames > 1
                ? numberOfFrames
                : files.length

          const item = {
            imageId: imageIds[i],
            instanceNumber: instanceNumber,
            name:
              this.props.origin === 'local'
                ? getFileNameCorrect(file.name)
                : file.name,
            image: image,
            rows: rows,
            columns: columns,
            sliceDistance: sliceDistance,
            sliceLocation: sliceLocation,
            seriesInstanceUID: seriesInstanceUID || undefined,
            numberOfFrames: numberOfFrames > 0 ? numberOfFrames : undefined,
            patient: {
              patientName: patientName,
            },
            study: {
              studyId: studyId,
              studyDate: studyDate,
              studyTime: studyTime,
              studyDateTime: studyDateTime,
              studyDescription: studyDescription,
            },
            series: {
              seriesDate: seriesDate,
              seriesTime: seriesTime,
              seriesDescription: seriesDescription,
              seriesNumber: seriesNumber,
              echoNumber: echoNumber,
              seriesInstanceUID: seriesInstanceUID || undefined,
              totalImages,
              modality: modality || '',
              imageType: imageType || undefined,
            },
          }
          if (numberOfFrames > 1) {
            stampNumberOfFramesOnFile(item, numberOfFrames)
          }
          stampIsScoutOnFileItem(item)
          this.items.push(item)
          this.count++

          const progress = Math.floor(this.count * (100 / files.length))
          if (progress > this.nextProgress) {
            this.nextProgress += this.step
            this.setState({ progress, phase: 'load' })
          }

          const shouldPublish =
            this.count === 1 ||
            this.count - this._lastPublishCount >= 2 ||
            this.count === files.length
          if (shouldPublish) {
            this.publishItems(this.count === files.length)
          }

          if (this.count === files.length) {
            this.close()
          }
        },
        (e) => {
          console.log('Error in reading multiple files: ', e)
          this.count++
          if (this.count === files.length) {
            this.publishItems(true)
            this.close()
          }
        }
      )
    }
  }

  /**
   * Fija totalImages desde el índice previo (como manifest R2).
   * No usa el conteo parcial de items cargados como denominador.
   */
  applyIndexedTotals = (snapshot) => {
    snapshot.forEach((it) => {
      if (!it.series) it.series = {}
      const indexed = resolveSeriesTotalFromIndex(
        this._seriesIndex,
        it.seriesInstanceUID || (it.series && it.series.seriesInstanceUID),
        it.series.seriesNumber
      )
      if (indexed != null) {
        it.series.totalImages = Math.max(Number(it.series.totalImages) || 0, indexed)
      } else if (!it.series.totalImages || it.series.totalImages < 1) {
        // Sin índice: no usar el tamaño del estudio entero (infla el denominador).
        // Dejar que el buffer actual sea piso hasta el publish final.
        it.series.totalImages = Math.max(
          Number(it.series.totalImages) || 0,
          1
        )
      }
    })
  }

  notifyProgress = (extra = {}) => {
    if (typeof this.props.onProgress !== 'function') return
    const total =
      (this.props.files && this.props.files.length) || this.count || 0
    // phase explícita: setState('load') puede no haber flusheado aún
    const phase =
      extra.phase ||
      (extra.published != null || extra.files ? 'load' : null) ||
      this.state.phase ||
      'load'
    this.props.onProgress({
      phase,
      completed: this.count,
      totalTasks: total,
      progress: this.state.progress,
      ...extra,
    })
  }

  publishItems = (final) => {
    if (!this.items || this.items.length === 0) return
    let snapshot = this.items.slice()
    if (final) {
      snapshot.sort(
        (l, r) => (l.instanceNumber || 0) - (r.instanceNumber || 0)
      )
    }
    this.applyIndexedTotals(snapshot)
    if (final && !this._seriesIndex) {
      applySeriesTotals(snapshot)
    }
    this._lastPublishCount = this.count
    this.props.setFilesStore(snapshot)
    this.notifyProgress({
      done: !!final,
      published: snapshot.length,
      files: snapshot,
    })
    if (final) {
      this.t1 = performance.now()
      console.log(
        `performance load ${this.count} images in ${this.t1 - this.t0} milliseconds`
      )
    }
  }

  close = () => {
    this.props.onClose()
  }

  cancel = () => {
    this.setState({cancel: true}) 
  }

  render() {
    // Carga en segundo plano: sin modal (ZIP / multi-local)
    if (this.props.silent) {
      return null
    }
    const phaseLabel =
      this.state.phase === 'index'
        ? t('dialogs.indexingSeries')
        : t('dialogs.openingMultiple')
    return (
      <div>
        <Dialog
            open={true}
            onClose={this.close}
            aria-labelledby="alert-dialog-title"
        >
            <DialogTitle id="alert-dialog-title">{phaseLabel}</DialogTitle>
            <DialogContent>
              {this.state.phase === 'index' ? (
                <DialogContentText>
                  {t('dialogs.indexingSeriesHint')}
                </DialogContentText>
              ) : null}
              <LinearProgress variant="determinate" value={this.state.progress} color='secondary' />
            </DialogContent>
            <DialogActions>
                <Button onClick={this.cancel}>
                    {t('common.cancel')}
                </Button>
            </DialogActions>
        </Dialog>
      </div>
    )
  }
}

const mapDispatchToProps = (dispatch) => {
  return {
      setFilesStore: (files) => dispatch(filesStore(files)),
  }
}

export default connect(null, mapDispatchToProps, null, { forwardRef: true })(
  OpenMultipleFilesDlg
)
